import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Box, Typography, Paper, CircularProgress, LinearProgress, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Button,
} from "@mui/material";
import { getPoll, getPollVoteSummary, getPollVoteDetails } from "../firebaseService";

const PollResultsPage = () => {
  const { pollId } = useParams();
  const [poll, setPoll] = useState(null);
  const [votes, setVotes] = useState({});
  const [voteDetails, setVoteDetails] = useState([]);
  const [showDetails, setShowDetails] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      const [pollData, summary, details] = await Promise.all([
        getPoll(pollId),
        getPollVoteSummary(pollId),
        getPollVoteDetails(pollId),
      ]);
      setPoll(pollData);
      setVotes(summary);
      setVoteDetails(details.sort((a, b) => a.name.localeCompare(b.name)));
      setLoading(false);
    };
    if (pollId) load();
  }, [pollId]);

  if (loading) {
    return <Box sx={{ textAlign: "center", mt: 4 }}><CircularProgress /></Box>;
  }

  if (!poll) {
    return <Typography variant="h6" sx={{ textAlign: "center", mt: 4 }}>Abstimmung nicht gefunden.</Typography>;
  }

  const totalVotes = Object.values(votes).reduce((sum, val) => sum + val, 0);

  // Alle Optionen anzeigen (auch mit 0 Stimmen)
  const allOptions = poll.options.map((opt) => ({
    label: opt,
    count: votes[opt] || 0,
  })).sort((a, b) => b.count - a.count);

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        {poll.emoji && `${poll.emoji} `}{poll.title}: Ergebnisse
      </Typography>

      {poll.description && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{poll.description}</Typography>
      )}

      <Paper elevation={3} sx={{ p: 3, mt: 2 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
          <Typography variant="subtitle1">
            Gesamt: {totalVotes} Stimme{totalVotes !== 1 ? "n" : ""}
          </Typography>
          <Chip
            label={poll.published ? "Aktiv" : "Geschlossen"}
            color={poll.published ? "success" : "default"}
            size="small"
          />
        </Box>

        {allOptions.map(({ label, count }) => {
          const percent = totalVotes > 0 ? ((count / totalVotes) * 100).toFixed(1) : 0;
          return (
            <Box key={label} sx={{ mb: 3 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                <Typography variant="body1" fontWeight="bold">{label}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {count} Stimme{count !== 1 ? "n" : ""} ({percent}%)
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={parseFloat(percent)}
                sx={{
                  height: 10,
                  borderRadius: 5,
                  backgroundColor: "rgba(255,255,255,0.1)",
                  "& .MuiLinearProgress-bar": {
                    backgroundColor: poll.color || "#5b8def",
                  },
                }}
              />
            </Box>
          );
        })}
      </Paper>

      {/* Detaillierte Stimmen */}
      <Box sx={{ mt: 2 }}>
        <Button
          variant="outlined"
          onClick={() => setShowDetails(!showDetails)}
          sx={{ mb: 2 }}
        >
          {showDetails ? "Details ausblenden" : "Einzelne Stimmen anzeigen"}
        </Button>

        {showDetails && (
          <Paper elevation={3}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Stimme</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Zeitpunkt</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {voteDetails.map((v) => (
                    <TableRow key={v.memberId}>
                      <TableCell>{v.name}</TableCell>
                      <TableCell>
                        <Chip
                          label={v.vote}
                          size="small"
                          sx={{ bgcolor: `${poll.color || "#5b8def"}22`, fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell sx={{ color: "text.secondary", fontSize: "0.85rem" }}>
                        {v.timestamp ? new Date(v.timestamp).toLocaleString("de-DE") : "—"}
                      </TableCell>
                    </TableRow>
                  ))}
                  {voteDetails.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={3} align="center" sx={{ color: "text.secondary" }}>
                        Noch keine Stimmen abgegeben.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}
      </Box>
    </Box>
  );
};

export default PollResultsPage;
